import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Fade,
  Grow,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Circles } from "react-loader-spinner";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";
import { AssetFinanceUrl } from "../../../config";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import useSubRightPermissionsHook from "../../../data/RBAC/SubRightPermissionHook";
import { BRAND_BLUE, BRAND_BLUE_LIGHT, assetColumns, exportToCsv } from "../stageBoardShared";
import SettleRepossessedAssetDialog from "./SettleRepossessedAssetDialog";
import InitiateDefaultDialog from "./InitiateDefaultDialog";

const base_url = AssetFinanceUrl.uri;

const STAGES = [
  {
    key: "DEFAULTED",
    title: "Defaulted Assets",
    description: "Repossessed collateral pending disposal",
    icon: ReportProblemIcon,
    accent: "error",
    objectName: "collateralAssetByColumnValues",
    inputObjectName: "asset",
    parameters: { assetStatus: "REPOSSESSED"},
  },
  {
    key: "AUCTIONED",
    title: "Auctioned Assets",
    description: "Assets disposed of via auction — process complete",
    icon: LocalOfferIcon,
    accent: "info",
    objectName: "collateralAssetByColumnValues",
    inputObjectName: "asset",
    parameters: { assetStatus: "DISPOSED" },
  },
];

const AssetRecovery = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { hasSubRightPermission } = useSubRightPermissionsHook();

  const [activeIndex, setActiveIndex] = useState(0);
  const [dataByStage, setDataByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [settleRow, setSettleRow] = useState(null);
  const [initiateDefaultOpen, setInitiateDefaultOpen] = useState(false);

  const stage = STAGES[activeIndex];

  const canDisposeAsset = hasSubRightPermission("Defaulted Assets", "ActivatePermission");
  const canInitiateDefault = hasSubRightPermission("Defaulted Assets", "CreatePermission");
  const canExportStage = hasSubRightPermission(stage.title, "ExportPermission");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        STAGES.map((s) => fetchJsonDynamic(s.objectName, s.inputObjectName, s.parameters, base_url)),
      );
      const next = {};
      responses.forEach((res, idx) => {
        const key = STAGES[idx].key;
        let rows = [];
        if (res && !res.error && res.results) {
          try {
            const parsed = JSON.parse(res.results);
            rows = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            rows = [];
          }
        }
        next[key] = { rows, error: res?.error || null };
      });
      setDataByStage(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const columnsForActiveStage = useMemo(() => {
    if (stage.key === "DEFAULTED") {
      return [
        ...assetColumns,
        {
          field: "actions",
          headerName: "Actions",
          width: 190,
          sortable: false,
          filterable: false,
          renderCell: (params) =>
            canDisposeAsset ? (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<LocalOfferIcon fontSize="small" />}
                onClick={() => setSettleRow(params.row)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "6px",
                  "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 10px rgba(210,40,54,0.35)" },
                  transition: "all 0.18s ease",
                }}
              >
                Settle Asset
              </Button>
            ) : (
              <Chip label="No access" size="small" variant="outlined" />
            ),
        },
      ];
    }
    return assetColumns;
  }, [stage.key, canDisposeAsset]);

  const filteredRows = useMemo(() => {
    const rows = dataByStage[stage.key]?.rows || [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q)),
    );
  }, [dataByStage, stage.key, search]);

  const handleExport = () => {
    exportToCsv(filteredRows, columnsForActiveStage, `${stage.key.toLowerCase()}.csv`);
  };

  return (
    <Box m="20px">
      <Header
        title="Asset Recovery"
        subtitle="Manage repossessed collateral through to auction disposal"
      />

      {/* Process stepper */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 1,
          mb: 3,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {STAGES.map((s, idx) => {
          const StageIcon = s.icon;
          const isActive = idx === activeIndex;
          const count = dataByStage[s.key]?.rows.length ?? 0;
          return (
            <React.Fragment key={s.key}>
              <Grow in timeout={280 + idx * 120}>
                <Paper
                  onClick={() => setActiveIndex(idx)}
                  elevation={isActive ? 6 : 1}
                  sx={{
                    flex: 1,
                    minWidth: 210,
                    cursor: "pointer",
                    p: 2,
                    borderRadius: "10px",
                    position: "relative",
                    overflow: "hidden",
                    border: isActive
                      ? `2px solid ${BRAND_BLUE}`
                      : `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e4eaf3"}`,
                    background: isActive
                      ? `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`
                      : isDark
                        ? "#162736"
                        : "#ffffff",
                    color: isActive ? "#fff" : undefined,
                    transition: "all 0.22s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: isActive
                        ? "0 8px 20px rgba(23,102,162,0.4)"
                        : "0 6px 16px rgba(23,102,162,0.15)",
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isActive ? "rgba(255,255,255,0.2)" : `${colors.blueAccent[isDark ? 700 : 900]}`,
                        color: isActive ? "#fff" : BRAND_BLUE,
                      }}
                    >
                      <StageIcon fontSize="small" />
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: isActive ? "rgba(255,255,255,0.22)" : undefined,
                        color: isActive ? "#fff" : undefined,
                      }}
                      color={isActive ? undefined : s.accent}
                    />
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={700} mt={1.5}>
                    {s.title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: isActive ? 0.85 : 0.65, display: "block", mt: 0.3 }}>
                    {s.description}
                  </Typography>

                  {s.key === "DEFAULTED" && count > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: isActive ? "#fff" : "#ef4444",
                        animation: "pulse 1.6s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%": { boxShadow: "0 0 0 0 rgba(239,68,68,0.55)" },
                          "70%": { boxShadow: "0 0 0 8px rgba(239,68,68,0)" },
                          "100%": { boxShadow: "0 0 0 0 rgba(239,68,68,0)" },
                        },
                      }}
                    />
                  )}
                </Paper>
              </Grow>
              {idx < STAGES.length - 1 && (
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    color: colors.grey[500],
                  }}
                >
                  <ChevronRightIcon />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Stage table */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e4eaf3"}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            p: 2,
            background: isDark
              ? "linear-gradient(135deg, #122033 0%, #1a3a54 100%)"
              : `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
            color: "#fff",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {stage.title}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {stage.description} • {filteredRows.length} record{filteredRows.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {stage.key === "DEFAULTED" && canInitiateDefault && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={() => setInitiateDefaultOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  bgcolor: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.26)" },
                }}
              >
                Initiate Default
              </Button>
            )}
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255,255,255,0.7)" }} fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "rgba(255,255,255,0.14)",
                  borderRadius: "8px",
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& input::placeholder": { color: "rgba(255,255,255,0.7)", opacity: 1 },
                },
              }}
              sx={{ minWidth: 200 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAll} sx={{ color: "#fff" }}>
                <RefreshIcon
                  sx={{
                    animation: loading ? "spin 0.9s linear infinite" : "none",
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            {canExportStage && (
              <Tooltip title="Export CSV">
                <IconButton onClick={handleExport} sx={{ color: "#fff" }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Box sx={{ position: "relative", minHeight: 360 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 360 }}>
              <Circles height="60" width="60" color={BRAND_BLUE} ariaLabel="loading" />
            </Box>
          ) : (
            <Fade in timeout={400} key={stage.key}>
              <Box>
                <DataGrid
                  autoHeight
                  rows={filteredRows}
                  columns={columnsForActiveStage}
                  getRowId={(row) => row.Id ?? Math.random()}
                  disableSelectionOnClick
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 220 }}>
                        <Typography color="text.secondary">No records at this stage</Typography>
                      </Box>
                    ),
                  }}
                  sx={{
                    border: "none",
                    fontSize: "0.8rem",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: isDark ? "#122033" : "#1d3a5c",
                      color: "#fff",
                      borderBottom: "none",
                      fontSize: "0.71rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                      color: "rgba(255,255,255,0.75)",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.025)" : "#f4f7fb",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: isDark ? "rgba(23,102,162,0.18)" : "#e8f0fe",
                      transition: "background-color 0.14s ease",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(23,102,162,0.15)"}`,
                    },
                  }}
                />
              </Box>
            </Fade>
          )}
        </Box>
      </Paper>

      {settleRow && (
        <SettleRepossessedAssetDialog
          open={Boolean(settleRow)}
          row={settleRow}
          base_url={base_url}
          onClose={() => setSettleRow(null)}
          onSuccess={fetchAll}
        />
      )}

      {initiateDefaultOpen && (
        <InitiateDefaultDialog
          open={initiateDefaultOpen}
          base_url={base_url}
          onClose={() => setInitiateDefaultOpen(false)}
          onSuccess={fetchAll}
        />
      )}
    </Box>
  );
};

export default AssetRecovery;
